import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import StarRating from './StarRating';
import { toast } from '../hooks/use-toast';

const ReviewModal = ({ isOpen, onClose, ride, userRole, onSubmitSuccess }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen || !ride) return null;

  const handleSubmit = async () => {
    if (rating === 0) {
      toast({
        title: 'Rating Required',
        description: 'Please select a star rating',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);

    try {
      const api = (await import('../utils/api')).default;
      
      await api.post('/reviews/submit', {
        rideId: ride.id,
        rating,
        comment,
      });

      toast({
        title: 'Review Submitted',
        description: 'Thank you for your feedback!',
      });

      if (onSubmitSuccess) onSubmitSuccess();
      onClose();
    } catch (error) {
      console.error('Error submitting review:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to submit review',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const recipientLabel = userRole === 'rider' ? 'driver' : 'passenger';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X className="h-5 w-5" />
        </button>

        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Rate Your {recipientLabel === 'driver' ? 'Driver' : 'Ride'}
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          How was your experience with this {recipientLabel}?
        </p>

        {/* Ride Details */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <p className="text-sm text-gray-600 mb-1">
            {ride.type === 'delivery' ? 'Delivery' : 'Ride'} Details
          </p>
          <p className="font-medium text-gray-900">{ride.pickup}</p>
          <p className="text-sm text-gray-600 mt-1">to</p>
          <p className="font-medium text-gray-900">{ride.dropoff}</p>
          <p className="text-sm text-gray-500 mt-2">
            K{ride.price?.toFixed(2)}
          </p>
        </div>

        {/* Star Rating */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Your Rating
          </label>
          <StarRating rating={rating} onRatingChange={setRating} size="large" />
        </div>

        {/* Comment */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Comments (Optional)
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={`Share your experience with this ${recipientLabel}...`}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none"
            rows={4}
            maxLength={500}
          />
          <p className="text-xs text-gray-500 mt-1">
            {comment.length}/500 characters
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1"
            disabled={submitting}
          >
            Skip for Now
          </Button>
          <Button
            onClick={handleSubmit}
            className="flex-1 bg-pink-600 hover:bg-pink-700"
            disabled={submitting || rating === 0}
          >
            {submitting ? 'Submitting...' : 'Submit Review'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default ReviewModal;
